import type { ReasoningRow } from '../../meta/types';

/** A single ungrouped row, or ≥2 rows that resolved to the same tool and should
 *  render as one expandable, counted line (OMNI-449). Same-tool rows group even
 *  when other steps are interleaved between them. */
export type RenderItem =
  | { readonly kind: 'single'; readonly row: ReasoningRow }
  | {
      readonly kind: 'group';
      readonly key: string;
      /** The line shown for the group: the server's own wording. */
      readonly label: string;
      readonly rows: readonly ReasoningRow[];
    };

/** Grouping key for a row, or null when the row must never be grouped. */
export function groupKeyOf(row: ReasoningRow): string | null {
  if (row.kind === 'tool') {
    return row.toolName.length > 0 ? row.toolName : null;
  }
  if (row.kind === 'reasoning') {
    // Empty string is not a usable key — treat it as ungroupable, the same way
    // tool rows treat an empty toolName (an empty key must never group rows).
    const key = row.groupKey ?? '';
    return key.length > 0 ? key : null;
  }
  return null;
}

/** The line a group of repeated calls shows. The server words it (each step
 *  carries `groupLabel`, the action without what each call was about) and
 *  decides which calls repeat (`groupKey`); this never guesses wording. A
 *  server that predates `groupLabel` gets the first call's own line. */
export function groupLabelOf(rows: readonly ReasoningRow[]): string {
  const [first] = rows;
  if (first === undefined) {
    return '';
  }
  if (first.kind === 'reasoning' && first.groupLabel) {
    return first.groupLabel;
  }
  return rowText(first);
}

/** The line text a row renders. */
export function rowText(row: ReasoningRow): string {
  if (row.kind === 'tool') {
    return row.displayName;
  }
  if (row.kind === 'reasoning') {
    return row.text;
  }
  return `Used the ${row.skillName.replaceAll(/[-_]+/g, ' ').trim()} skill`;
}

/** Fold a flat row list into render items, collapsing every set of ≥2 rows that
 *  share a non-null group key into one group — even when other steps are
 *  interleaved between them. Iterating a real Cyber Pulse turn calls one matrix
 *  tool repeatedly while threading `list_*` / `describe_*` calls in between, so
 *  requiring strict adjacency left each repeat as its own line and the trail
 *  still read as a stuck loop (OMNI-449). Each group is anchored at the position
 *  of its first occurrence and absorbs every later same-key row; the surrounding
 *  interleaved steps keep their own positions. Rows with a null key (skill rows,
 *  plain reasoning steps) always render on their own. */
export function groupSameToolRows(rows: readonly ReasoningRow[]): RenderItem[] {
  // One slot per output position, in first-occurrence order. A keyed slot holds
  // the running list of rows sharing that key; later same-key rows append to the
  // existing slot rather than opening a new one.
  type Slot =
    | { readonly kind: 'single'; readonly row: ReasoningRow }
    | { readonly kind: 'keyed'; readonly key: string; rows: ReasoningRow[] };

  const slots: Slot[] = [];
  const slotByKey = new Map<string, Extract<Slot, { kind: 'keyed' }>>();

  for (const row of rows) {
    const key = groupKeyOf(row);
    if (key === null) {
      slots.push({ kind: 'single', row });
      continue;
    }
    const existing = slotByKey.get(key);
    if (existing) {
      existing.rows.push(row);
      continue;
    }
    const slot: Extract<Slot, { kind: 'keyed' }> = {
      kind: 'keyed',
      key,
      rows: [row],
    };
    slotByKey.set(key, slot);
    slots.push(slot);
  }

  return slots.map((slot): RenderItem => {
    if (slot.kind === 'single') {
      return { kind: 'single', row: slot.row };
    }
    // A key seen only once is a single line, never a group of one.
    const [first] = slot.rows;
    if (first !== undefined && slot.rows.length < 2) {
      return { kind: 'single', row: first };
    }
    return {
      kind: 'group',
      key: slot.key,
      label: groupLabelOf(slot.rows),
      rows: slot.rows,
    };
  });
}
